import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Pencil, Plus, Receipt, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { isoDate } from '../../utils/time';
import {
  PAGE_WIDE, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, OverflowMenu, Dialog, Field, FormRow,
  money, num, fmtDate,
} from '../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, TFOOT_ROW, MoneyCell, DateRange, codeLabel,
} from './financeKit';

const EMPTY = { category_id: '', name: '', amount: '', expense_date: isoDate(), notes: '' };

const day = (v) => fmtDate(v ? String(v).slice(0, 10) : '');

// Add / edit an expense. The body sent is exactly what this page has always sent.
function ExpenseDialog({ open, editing, initial, categories, categoriesFailed, onRetryCategories, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setForm(initial); setErrors({}); } }, [open, initial]);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: undefined }));
  };

  async function save(e) {
    if (e) e.preventDefault();
    const next = {};
    if (!form.name) next.name = 'Enter a name for the expense.';
    if (!form.amount) next.amount = 'Enter the amount.';
    if (next.name || next.amount) { setErrors(next); return; }
    setSaving(true);
    try {
      const body = {
        category_id: form.category_id || null,
        name: form.name,
        amount: parseFloat(form.amount),
        expense_date: form.expense_date,
        notes: form.notes || null,
      };
      if (editing) {
        await api.put(`/financials/expenses/${editing}`, body);
        toast.success('Expense updated');
      } else {
        await api.post('/financials/expenses', body);
        toast.success('Expense added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't save the expense. Check your connection, then try again.");
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title={editing ? 'Edit expense' : 'Add expense'}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="expense-form" disabled={saving} className="btn-primary">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? 'Saving…' : 'Save expense'}
          </button>
        </>
      )}>
      <form id="expense-form" onSubmit={save} className="space-y-3.5" noValidate>
        <Field label="Name" required error={errors.name}>
          {(id) => (
            <input id={id} type="text" className="input-field" value={form.name} aria-invalid={!!errors.name}
              onChange={e => set('name', e.target.value)} placeholder="For example, office rent" />
          )}
        </Field>
        <FormRow>
          <Field label="Amount ($)" required error={errors.amount}>
            {(id) => (
              <input id={id} type="number" step="0.01" min="0" inputMode="decimal" className="input-field tabular-nums"
                value={form.amount} aria-invalid={!!errors.amount}
                onChange={e => set('amount', e.target.value)} placeholder="0.00" />
            )}
          </Field>
          <Field label="Date">
            {(id) => <input id={id} type="date" className="input-field" value={form.expense_date} onChange={e => set('expense_date', e.target.value)} />}
          </Field>
        </FormRow>
        <Field label="Category"
          hint={categoriesFailed ? "Categories didn't load, so only Uncategorised is available." : undefined}
          action={categoriesFailed ? <button type="button" onClick={onRetryCategories} className="text-[12px] font-medium text-primary-700 hover:underline">Try again</button> : null}>
          {(id) => (
            <select id={id} className="input-field" value={form.category_id} onChange={e => set('category_id', e.target.value)}>
              <option value="">Uncategorised</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </Field>
        <Field label="Notes">
          {(id) => <input id={id} type="text" className="input-field" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional" />}
        </Field>
      </form>
    </Dialog>
  );
}

export default function Expenses() {
  const confirm = useConfirm();
  const [expenses, setExpenses]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesFailed, setCategoriesFailed] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [initial, setInitial]       = useState(EMPTY);
  const [editing, setEditing]       = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [from, setFrom]             = useState(() => {
    const d = new Date(); d.setDate(1); return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(new Date()));

  const loadCategories = useCallback(async () => {
    try {
      const r = await api.get('/financials/expense-categories');
      setCategories(r.data || []);
      setCategoriesFailed(false);
    } catch { setCategoriesFailed(true); }
  }, []);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/financials/expenses?from=${from}&to=${to}`);
      setExpenses(r.data || []);
    } catch (e) {
      setExpenses([]);
      setError(e.response?.data?.message || "We couldn't load expenses. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadExpenses(); }, [loadExpenses]);

  function openNew() { setInitial(EMPTY); setEditing(null); setShowForm(true); }
  function openEdit(exp) {
    setInitial({
      category_id: exp.category_id || '',
      name: exp.name,
      amount: exp.amount,
      expense_date: exp.expense_date?.slice(0,10) || '',
      notes: exp.notes || '',
    });
    setEditing(exp.id);
    setShowForm(true);
  }
  const closeForm = useCallback(() => setShowForm(false), []);
  const onSaved = useCallback(() => { setShowForm(false); loadExpenses(); }, [loadExpenses]);

  async function del(exp) {
    const ok = await confirm(
      `${exp.name || 'This expense'}, ${money(exp.amount)} on ${day(exp.expense_date)}. Deleting it removes it from the expense totals and the profit reports. This can't be undone.`,
      { title: 'Delete this expense?', confirmLabel: 'Delete expense', danger: true },
    );
    if (!ok) return;
    try {
      await api.delete(`/financials/expenses/${exp.id}`);
      toast.success('Expense deleted');
      loadExpenses();
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't delete the expense. Try again."); }
  }

  const total = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const avg = expenses.length ? total / expenses.length : 0;
  const largest = expenses.reduce((m, e) => Math.max(m, parseFloat(e.amount || 0)), 0);

  return (
    <div className={PAGE_WIDE}>
      {/* The top bar already names the page; this row holds its controls. */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="expenses" />
        <button type="button" onClick={openNew} className="btn-primary">
          <Plus size={15} /> Add expense
        </button>
      </div>

      {error ? (
        <div className="card"><ErrorState message={error} onRetry={loadExpenses} /></div>
      ) : (
        <>
          {/* Summary — the total equals the table's totals row. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Total expenses" value={loading ? '…' : money(total)} accent="primary"
              sub={loading ? null : 'In this range'} />
            <KpiTile label="Entries" value={loading ? '…' : num(expenses.length)} accent="slate"
              sub={loading ? null : 'Logged'} />
            <KpiTile label="Average" value={loading ? '…' : money(avg)} accent="slate"
              sub={loading ? null : 'Per expense'} />
            <KpiTile label="Largest" value={loading ? '…' : money(largest)} accent="slate"
              sub={loading ? null : 'Single expense'} />
          </div>

          <SectionCard title={`Logged expenses${loading ? '' : ` · ${expenses.length}`}`} icon={Receipt}>
            {loading ? (
              <TableSkeleton rows={6} cols={5} />
            ) : expenses.length === 0 ? (
              <EmptyState icon={Receipt} title="No expenses in this range"
                hint="Log business costs such as rent, insurance and software, so the profit reports take them off revenue."
                action={<button type="button" onClick={openNew} className="btn-primary"><Plus size={15} /> Add expense</button>} />
            ) : (
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th scope="col" className={TH}>Date</th>
                      <th scope="col" className={TH}>Category</th>
                      <th scope="col" className={TH}>Name</th>
                      <th scope="col" className={TH_NUM}>Amount</th>
                      <th scope="col" className={TH}>Notes</th>
                      <th scope="col" className={`${TH} w-12`}><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map(exp => (
                      <tr key={exp.id} className={TR}>
                        <td className={`${TD} whitespace-nowrap`}>{day(exp.expense_date)}</td>
                        <td className={TD}>
                          <span className="badge-gray">{codeLabel(exp.category_name) || 'Uncategorised'}</span>
                        </td>
                        <td className={`${TD} font-medium text-slate-800`}>{exp.name}</td>
                        <td className={TD_NUM}><MoneyCell value={exp.amount} /></td>
                        <td className={`${TD} text-[13px] text-slate-500`}>{exp.notes || '—'}</td>
                        <td className={`${TD} text-right`}>
                          <OverflowMenu label={`Actions for ${exp.name || 'expense'}`} size={16} className="p-2.5" items={[
                            { label: 'Edit expense', icon: Pencil, onClick: () => openEdit(exp) },
                            { label: 'Delete expense', icon: Trash2, danger: true, onClick: () => del(exp) },
                          ]} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={TFOOT_ROW}>
                      <td className={TD} colSpan={3}>Total</td>
                      <td className={TD_NUM}><MoneyCell value={total} strong /></td>
                      <td className={TD} colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}

      <ExpenseDialog open={showForm} editing={editing} initial={initial} categories={categories}
        categoriesFailed={categoriesFailed} onRetryCategories={loadCategories}
        onClose={closeForm} onSaved={onSaved} />
    </div>
  );
}
